<template>
    <PageTitle title="Caisses" description="Gérez vos caisses et ventes en temps réel">
        <template #actions>
            <button v-if="activeCashRegister" class="close-register-btn" @click="openCloseModal(activeCashRegister)">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                    <line x1="9" y1="9" x2="15" y2="15"></line>
                    <line x1="15" y1="9" x2="9" y2="15"></line>
                </svg>
                Fermer la caisse
            </button>
        </template>
    </PageTitle>

    <div class="cash-register-page">
        <!-- État actuel de la caisse -->
        <div class="current-register-section">
            <div v-if="activeCashRegister" class="register-active">
                <!-- En-tête avec statut et CA -->
                <div class="register-status">
                    <div class="status-info">
                        <div class="status-indicator">
                            <div class="status-dot"></div>
                            <span class="status-text">Caisse ouverte</span>
                        </div>
                        <div class="register-date">{{ formatDate(activeCashRegister.start_date) }}</div>
                    </div>
                    <div class="ca-display">
                        <span class="ca-amount">{{ formatCurrency(cashRegisterStats?.ca?.ttc || 0) }}</span>
                        <span class="ca-label">CA Total</span>
                    </div>
                </div>

                <!-- Statistiques rapides -->
                <div class="quick-stats">
                    <div class="stat-item">
                        <span class="stat-number">{{ formatCurrency(activeCashRegister.start_balance) }}</span>
                        <span class="stat-label">Fond initial</span>
                    </div>
                    <div class="stat-item">
                        <span class="stat-number">{{ cashRegisterStats?.sales?.total || 0 }}</span>
                        <span class="stat-label">Ventes totales</span>
                    </div>
                    <div class="stat-item">
                        <span class="stat-number">{{ cashRegisterStats?.sales?.completed || 0 }}</span>
                        <span class="stat-label">Complétées</span>
                    </div>
                    <div class="stat-item">
                        <span class="stat-number">{{ cashRegisterStats?.sales?.pending || 0 }}</span>
                        <span class="stat-label">En attente</span>
                    </div>
                </div>

                <!-- Actions de vente -->
                <div class="sales-actions">
                    <button class="sale-button primary" @click="onMakeCashSale">
                        <div class="sale-icon">
                            <img src="@/assets/images/cash-register/sale-simple.png" alt="Vente Comptant" />
                        </div>
                        <div class="sale-content">
                            <h2>Vente Comptant</h2>
                            <p>Paiement immédiat</p>
                            <span class="sale-amount">{{ formatCurrency(cashRegisterStats?.by_type?.single?.ca_ttc || 0)
                                }}</span>
                        </div>
                    </button>

                    <button class="sale-button secondary" @click="onMakeDeferredSale">
                        <div class="sale-icon">
                            <img src="@/assets/images/cash-register/sale-differed.png" alt="Vente Différée" />
                        </div>
                        <div class="sale-content">
                            <h2>Bons de commande</h2>
                            <p>Paiement ultérieur</p>
                            <span class="sale-amount">{{ formatCurrency(cashRegisterStats?.by_type?.deferred?.ca_ttc ||
                                0) }}</span>
                        </div>
                    </button>
                </div>
            </div>

            <!-- Aucune caisse ouverte -->
            <div v-else class="no-register">
                <div class="empty-state">
                    <div class="empty-icon">
                        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                            stroke-width="1.5">
                            <rect x="2" y="7" width="20" height="14" rx="2" ry="2"></rect>
                            <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"></path>
                        </svg>
                    </div>
                    <h3>Aucune caisse ouverte</h3>
                    <p>Ouvrez une caisse pour commencer vos ventes</p>
                    <button class="open-register-btn" @click="showOpenModal = true">
                        Ouvrir une caisse
                    </button>
                </div>
            </div>
        </div>

        <!-- Dernières ventes -->
        <div class="recent-sales-section" v-if="activeCashRegister">
            <div class="section-header">
                <h3>Dernières ventes</h3>
                <span class="sales-count">{{ lastSales.length }} ventes récentes</span>
            </div>

            <div v-if="loadingSales" class="loading-state">
                <div class="loading-spinner"></div>
                <p>Chargement...</p>
            </div>

            <div v-else-if="lastSales.length === 0" class="empty-sales">
                <p>Aucune vente aujourd'hui</p>
            </div>

            <div v-else class="sales-list">
                <div v-for="sale in lastSales" :key="sale.id" class="sale-row">
                    <div class="sale-info">
                        <span class="sale-id">#{{ sale.invoice_number }}</span>
                        <span class="sale-date">{{ formatShortDate(sale.sale_date || sale.created_at) }}</span>
                        <span class="sale-user">{{ sale.user?.name }}</span>
                    </div>
                    <div class="sale-amount">{{ formatCurrency(sale.total_incl_tax ?? sale.total) }}</div>
                    <div class="sale-status">
                        <SaleStatusBadge :sale="sale" />
                    </div>
                    <div class="sale-payment">{{ extractPaymentMethods(sale.payments) || 'Espèces' }}</div>
                    <div class="sale-actions">
                        <button class="action-btn view-btn" @click="viewSaleDetails(sale)" title="Voir détails">
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                                stroke-width="2">
                                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                                <circle cx="12" cy="12" r="3"></circle>
                            </svg>
                        </button>
                        <button class="action-btn print-btn" @click="printTicket(sale)" title="Imprimer ticket">
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                                stroke-width="2">
                                <polyline points="6,9 6,2 18,2 18,9"></polyline>
                                <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2">
                                </path>
                                <rect x="6" y="14" width="12" height="8"></rect>
                            </svg>
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </div>

    <!-- Modales -->
    <OpenCashRegisterModal :show="showOpenModal" @close="showOpenModal = false" @success="onOpened" />
    <CloseCashRegisterModal :show="showCloseModal" :cash-register="selectedCashRegister" :statistics="cashRegisterStats"
        @close="closeCloseModal" @success="onClosed" />
</template>

<script>
import { ref, onMounted, defineExpose } from 'vue'
import { useRouter } from 'vue-router'
import CashRegisterService from '@/services/cashRegisterService'
import { salesService } from '@/services'
import OpenCashRegisterModal from '@/views/cashRegister/OpenCashRegisterModal.vue'
import CloseCashRegisterModal from '@/views/cashRegister/CloseCashRegisterModal.vue'
import PageTitle from '@/components/common/PageTitle.vue'
import SaleStatusBadge from '@/components/sales/SaleStatusBadge.vue'
import thermalPrinterService from '@/services/thermalPrinterService'
import { useToastStore } from '@/store/modules/toastStore'
export default {
    name: 'CashRegisterHome',
    components: {
        OpenCashRegisterModal,
        CloseCashRegisterModal,
        PageTitle,
        SaleStatusBadge
    },
    emits: ['register-changed'],
    setup(props, { emit }) {
        const router = useRouter()
        const activeCashRegister = ref(null)
        const cashRegisterStats = ref(null)
        const loadingStats = ref(false)
        const showOpenModal = ref(false)
        const showCloseModal = ref(false)
        const selectedCashRegister = ref(null)
        const lastSales = ref([])
        const loadingSales = ref(false)
        const toastStore = useToastStore()

        const formatDate = (date) => {
            if (!date) return '-'
            return new Date(date).toLocaleString('fr-FR', {
                year: 'numeric', month: 'short', day: 'numeric',
                hour: '2-digit', minute: '2-digit'
            })
        }

        const formatShortDate = (date) => {
            if (!date) return '-'
            return new Date(date).toLocaleString('fr-FR', {
                month: 'short', day: 'numeric',
                hour: '2-digit', minute: '2-digit'
            })
        }

        const formatCurrency = (amount) => {
            if (amount === null || amount === undefined) return '0 F'
            return new Intl.NumberFormat('fr-FR', {
                style: 'currency',
                currency: 'XOF',
                minimumFractionDigits: 0,
                currencyDisplay: 'symbol'
            }).format(amount).replace('XOF', 'F')
        }

        const viewSaleDetails = (sale) => {
            // Navigation vers la page de détails de la vente
            router.push({
                name: 'sale-details',
                params: { id: sale.id }
            })
        }

        const printTicket = async (sale) => {
            try {
                console.clear();
                console.log('sale:', sale)
                const saleResponse = await salesService.getSale(sale.id);
                console.log('sale RESPONSE:', saleResponse.data.data)
                await thermalPrinterService.printTicket(saleResponse.data.data, {
                    connectionType: 'usb',
                    shopInfo: JSON.parse(localStorage.getItem('company'))
                })
            } catch (error) {
                toastStore.addToast({
                    type: 'error',
                    message: error.message || 'Erreur lors de l\'impression du ticket',
                    duration: 4000
                })
            }
        }

        const fetchOpenedRegisterAndStats = async () => {
            loadingStats.value = true
            try {
                const opened = await CashRegisterService.getOpenedCashRegister()
                console.log('opened', opened)
                console.log(opened)
                activeCashRegister.value = opened || null
                if (activeCashRegister.value) {
                    const resp = await CashRegisterService.getCashRegisterStats(activeCashRegister.value.id)
                    cashRegisterStats.value = resp?.data?.statistics || null
                    await fetchLastSales()
                } else {
                    cashRegisterStats.value = null
                    lastSales.value = []
                    CashRegisterService.clearOpenedCashRegister()
                }
            } finally {
                loadingStats.value = false
            }
        }

        const extractPaymentMethods = (sale) => {
            return sale?.payment_label ?? '';
        }

        const fetchLastSales = async () => {
            if (!activeCashRegister.value) return
            loadingSales.value = true
            try {
                const response = await salesService.getSales({
                    cash_register_id: activeCashRegister.value.id,
                    per_page: 8,
                    page: 1,
                    sort_by: 'sale_date',
                    sort_direction: 'desc'
                })
                if (response?.data?.data) {
                    lastSales.value = response.data.data
                } else if (response?.data?.sales) {
                    lastSales.value = response.data.sales.slice(0, 10)
                } else if (Array.isArray(response?.data)) {
                    lastSales.value = response.data.slice(0, 10)
                } else {
                    lastSales.value = []
                }
            } catch (e) {
                lastSales.value = []
            } finally {
                loadingSales.value = false
            }
        }

        const onMakeCashSale = () => router.push({ name: 'sale-simple' })
        const onMakeDeferredSale = () => router.push({ name: 'sale-differe' })

        const openCloseModal = (cashRegister) => {
            if (!cashRegister) return
            selectedCashRegister.value = cashRegister
            showCloseModal.value = true
        }

        const closeCloseModal = () => {
            selectedCashRegister.value = null
            showCloseModal.value = false
        }

        const onOpened = async () => {
            await fetchOpenedRegisterAndStats()
            emit('register-changed')
        }

        const onClosed = async () => {
            await fetchOpenedRegisterAndStats()
            emit('register-changed')
        } 

        const CACHE_NAME = 'sale-buttons-v1'
        const EXPIRATION_TIME = 3 * 24 * 60 * 60 * 1000 

        const saleImages = [
            require('@/assets/images/cash-register/sale-simple.png'),
            require('@/assets/images/cash-register/sale-differed.png')
       ]
       

// fonction cacheSaleImages pour mettre en cache sur le navigateur les deux images des deux boutons
       async function cacheSaleImages () {
        const cache = await caches.open(CACHE_NAME)
        for (const url of saleImages) {
            const cached = await cache.match(url)
            const savedTime = localStorage.getItem(url)

            if (!cached || !savedTime || Date.now() - savedTime > EXPIRATION_TIME) {
                      const response = await fetch(url)
                      await cache.put(url, response.clone())
                      localStorage.setItem(url, Date.now())
                    }
                }
        }

        onMounted(() => {
            cacheSaleImages()
            fetchOpenedRegisterAndStats()
        })

        defineExpose({
            openCloseModal
        })

        return {
            // state
            activeCashRegister,
            cashRegisterStats,
            showOpenModal,
            showCloseModal,
            selectedCashRegister,
            lastSales,
            loadingSales,
            // methods
            formatDate,
            formatShortDate,
            formatCurrency,
            extractPaymentMethods,
            viewSaleDetails,
            printTicket,
            onMakeCashSale,
            onMakeDeferredSale,
            openCloseModal,
            closeCloseModal,
            onOpened,
            onClosed
        }
    }
}
</script>

<style scoped>
.cash-register-page {
    display: flex;
    flex-direction: column;
    gap: 24px;
}

.current-register-section {
    background: white;
    border-radius: 12px;
    padding: 24px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
    border: 1px solid #f1f5f9;
}

.register-active {
    display: flex;
    flex-direction: column;
    gap: 20px;
}

.register-status {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding-bottom: 20px;
    border-bottom: 1px solid #f1f5f9;
}

.status-info {
    display: flex;
    flex-direction: column;
    gap: 4px;
}

.status-indicator {
    display: flex;
    align-items: center;
    gap: 8px;
}

.status-dot {
    width: 8px;
    height: 8px;
    background: #10b981;
    border-radius: 50%;
    animation: pulse 2s infinite;
}

@keyframes pulse {

    0%,
    100% {
        opacity: 1;
    }

    50% {
        opacity: 0.6;
    }
}

.status-text {
    font-size: 16px;
    font-weight: 600;
    color: #065f46;
}

.register-date {
    font-size: 14px;
    color: #64748b;
}

.ca-display {
    text-align: right;
}

.ca-amount {
    display: block;
    font-size: 28px;
    font-weight: 700;
    color: #1e293b;
    line-height: 1;
}

.ca-label {
    font-size: 12px;
    color: #64748b;
    text-transform: uppercase;
    letter-spacing: 0.5px;
}

.quick-stats {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 16px;
}

.stat-item {
    background: #f8fafc;
    padding: 16px;
    border-radius: 8px;
    text-align: center;
    border: 1px solid #e2e8f0;
}

.stat-number {
    display: block;
    font-size: 18px;
    font-weight: 700;
    color: #1e293b;
    margin-bottom: 4px;
}

.stat-label {
    font-size: 12px;
    color: #64748b;
    text-transform: uppercase;
}

.sales-actions {
    display: flex;
    gap: 20px;
}

.sale-button {
    flex: 1;
    background: white;
    border: 2px solid #e2e8f0;
    border-radius: 12px;
    cursor: pointer;
    transition: all 0.3s ease;
    display: flex;
    align-items: center;
    gap: 16px;
    text-align: left;
}

.sale-button:hover {
    border-color: #0A5C6B;
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(10, 92, 107, 0.15);
    h1, h2 {
        color: white;
    }
    p {
        color: white;
    }
    span {
        color: white;
    }
}

.sale-button.primary:hover {
    background: linear-gradient(135deg, #0A5C6B, #094854);
    color: white;
}

.sale-button.secondary:hover {
    background: linear-gradient(135deg, #094854, #0A5C6B);
    color: white;
}

.sale-icon {
    flex-shrink: 0;
}

.sale-content h3 {
    font-size: 16px;
    font-weight: 600;
    margin: 0 0 4px 0;
    color: inherit;
}

.sale-content p {
    font-size: 13px;
    color: #64748b;
    margin: 0 0 8px 0;
}

.sale-button:hover .sale-content p {
    color: rgba(255, 255, 255, 0.8);
}

.sale-amount {
    font-size: 14px;
    font-weight: 600;
    color: #0A5C6B;
}

.sale-button:hover .sale-amount {
    color: white;
}

.close-register-btn {
    align-self: flex-start;
    padding: 10px 16px;
    background: #ef4444;
    color: white;
    border: none;
    border-radius: 8px;
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s;
    display: flex;
    align-items: center;
    gap: 8px;
}

.close-register-btn:hover {
    background: #dc2626;
    transform: translateY(-1px);
}

.no-register {
    padding: 40px 20px;
}

.empty-state {
    text-align: center;
    max-width: 400px;
    margin: 0 auto;
}

.empty-icon {
    color: #d1d5db;
    margin-bottom: 16px;
}

.empty-state h3 {
    font-size: 20px;
    font-weight: 600;
    color: #374151;
    margin: 0 0 8px 0;
}

.empty-state p {
    font-size: 14px;
    color: #6b7280;
    margin: 0 0 24px 0;
}

.open-register-btn {
    padding: 12px 24px;
    background: #0A5C6B;
    color: white;
    border: none;
    border-radius: 8px;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s;
}

.open-register-btn:hover {
    background: #094854;
    transform: translateY(-1px);
}

.recent-sales-section {
    background: white;
    border-radius: 12px;
    padding: 24px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
    border: 1px solid #f1f5f9;
}

.section-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 20px;
}

.section-header h3 {
    font-size: 18px;
    font-weight: 600;
    color: #1e293b;
    margin: 0;
}

.sales-count {
    font-size: 14px;
    color: #64748b;
}

.loading-state {
    text-align: center;
    padding: 40px 20px;
}

.loading-spinner {
    width: 32px;
    height: 32px;
    border: 3px solid #f3f4f6;
    border-top-color: #0A5C6B;
    border-radius: 50%;
    animation: spin 1s linear infinite;
    margin: 0 auto 12px;
}

@keyframes spin {
    to {
        transform: rotate(360deg);
    }
}

.empty-sales {
    text-align: center;
    padding: 40px 20px;
    color: #6b7280;
}

.sales-list {
    display: flex;
    flex-direction: column;
    gap: 12px;
}

.sale-row {
    display: grid;
    grid-template-columns: 1fr auto auto auto auto;
    gap: 16px;
    align-items: center;
    padding: 16px;
    background: #f8fafc;
    border-radius: 8px;
    border: 1px solid #e2e8f0;
    transition: all 0.2s;
}

.sale-row:hover {
    background: #f1f5f9;
    border-color: #cbd5e1;
}

.sale-info {
    display: flex;
    flex-direction: column;
    gap: 4px;
}

.sale-id {
    font-size: 14px;
    font-weight: 600;
    color: #1e293b;
}

.sale-date {
    font-size: 12px;
    color: #64748b;
}

.sale-amount {
    font-size: 14px;
    font-weight: 600;
    color: #0A5C6B;
}

.sale-status {
    text-align: center;
}

.sale-payment {
    font-size: 12px;
    color: #64748b;
    text-align: right;
}

.sale-user {
    font-size: 11px;
    color: #64748b;
    font-style: italic;
}

.sale-actions {
    display: flex;
    gap: 8px;
}

.action-btn {
    width: 32px;
    height: 32px;
    border: 1px solid #e2e8f0;
    background: white;
    border-radius: 6px;
    cursor: pointer;
    transition: all 0.2s;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #64748b;
}

.view-btn:hover {
    background: #0A5C6B;
    border-color: #0A5C6B;
    color: white;
    transform: translateY(-1px);
}

.print-btn:hover {
    background: #059669;
    border-color: #059669;
    color: white;
    transform: translateY(-1px);
}

.sale-icon {
    width: 20%;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
}

.sale-icon img {
    width:100%;
    height: 100%;
    object-fit: contain;
    border-radius: 10px 0 0 10px;
}

@media (max-width: 1024px) {
    .quick-stats {
        grid-template-columns: repeat(2, 1fr);
    }
}

@media (max-width: 768px) {
    .cash-register-page {
        padding: 16px;
    }

    .register-status {
        flex-direction: column;
        align-items: flex-start;
        gap: 16px;
    }

    .ca-display {
        text-align: left;
    }

    .quick-stats {
        grid-template-columns: 1fr 1fr;
    }

    .sales-actions {
        grid-template-columns: 1fr;
    }

    .sale-row {
        grid-template-columns: 1fr;
        gap: 12px;
    }

    .sale-payment {
        text-align: left;
    }
}

@media (max-width: 480px) {
    .quick-stats {
        grid-template-columns: 1fr;
    }
}
</style>